import axios from 'axios'
import moment from 'moment'
import 'moment/locale/cs'
import 'moment/locale/en-gb'
import jQuery from 'jquery'
import Swal, {
    SweetAlertIcon,
} from 'sweetalert2'

import i18n from './locales/i18n'

// Expose jQuery globally before Bootstrap 4 JS evaluates — its IIFE checks window.jQuery.
;(window as Window & typeof globalThis).jQuery = jQuery
;(window as Window & typeof globalThis).$ = jQuery

import 'bootstrap/dist/js/bootstrap.bundle.min.js'

declare global {
    interface Window {
        axios: typeof axios
        moment: typeof moment
        jQuery: typeof jQuery
        $: typeof jQuery
    }
}

type SweetType = 'success' | 'danger' | 'info' | 'warning' | 'error'

const coerceTitle = (text: unknown): string => {
    if (text === null || text === undefined) {
        return ''
    }
    if (typeof text === 'string') {
        return text
    }
    if (text instanceof Error) {
        return text.message
    }
    if (typeof text === 'object') {
        const data = (text as { data?: { message?: string }, message?: string }).data
        if (data && typeof data.message === 'string') {
            return data.message
        }
        const message = (text as { message?: string }).message
        if (typeof message === 'string') {
            return message
        }
    }
    return String(text)
}

/**
 * Map legacy `type` prop to a sweetalert2 icon.
 *
 * @param {SweetType|undefined} type
 * @returns {SweetAlertIcon}
 */
const mapTypeToIcon = (type?: SweetType): SweetAlertIcon => {
    switch (type) {
        case 'success':
            return 'success'
        case 'info':
            return 'info'
        case 'warning':
            return 'warning'
        case 'error':
            return 'error'
        case 'danger':
        default:
            return 'error'
    }
}

const preflight = (): void => {
    const userLang = window.location.pathname.substring(0, 4).replace('/', '').replace('/', '')

    moment.locale(userLang === 'en' ? 'en-gb' : userLang)
    window.moment = moment

    window.axios = axios

    window.axios.defaults.headers.common = {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'Accept-Language': userLang,
    }

    window.axios.interceptors.response.use(null, err => {
        if (err?.response?.status === 429) {
            return window.swalert(i18n.t('serverError.429'))
        }

        if (err?.response?.status === 500) {
            window.swalert(i18n.t('serverError.500'))
        }

        return Promise.reject(err.response)
    })

    /**
     * Call better alerts.
     * USAGE = anywhere in code window.swalert('FOO')  OR  window.swalert('FOO', 'success')
     *
     * @param {string} text
     * @param {SweetType} [type='danger'] - primary|info|success|warning|danger
     * @param {Function|null} [onConfirm=null]
     * @returns {Promise<SweetAlertResult>}
     */
    window.swalert = (text, type = 'danger', onConfirm = null) => Swal.fire({
        icon: mapTypeToIcon(type as SweetType),
        title: coerceTitle(text),
        confirmButtonText: 'Zavřít',
    }).then(result => {
        if (typeof onConfirm === 'function') {
            const returned: unknown = onConfirm()
            if (returned instanceof Promise) {
                return returned.then(() => result)
            }
        }
        return result
    })

    /**
     * Call better confirms.
     * USAGE = anywhere in code window.swconfirm({ --params-- })
     *
     * {
     *      title: 'Opravdu chcete smazat tuto položku?',   // required - string
     *      onConfirm: () => deleteGroup(group.slug),       // required - promise
     *      confirmBtnText: 'Ano',                          // optional - string
     *      cancelBtnText: 'Ne',                            // optional - string
     *      afterConfirm: () => loadGroups(),               // optional - promise
     *      type: 'danger',                                 // optional - string
     *  }
     *
     * @param {SwconfirmProps} props
     * @returns {Promise<SweetAlertResult>}
     */
    window.swconfirm = props => {
        const {
            title,
            onConfirm,
            confirmBtnText,
            cancelBtnText,
            afterConfirm,
            onCancel,
            type,
            showCancel,
        } = props

        return Swal.fire({
            icon: mapTypeToIcon(type as SweetType),
            title,
            showCancelButton: showCancel ?? true,
            confirmButtonText: confirmBtnText || 'Ano',
            cancelButtonText: cancelBtnText || 'Ne',
            preConfirm: () => Promise.resolve(onConfirm())
                .then(() => true)
                .catch((error: unknown) => {
                    Swal.showValidationMessage(String(error))
                    return false
                }),
        }).then(result => {
            if (result.isConfirmed) {
                if (typeof afterConfirm === 'function') {
                    const returned: unknown = afterConfirm()
                    if (returned instanceof Promise) {
                        return returned.then(() => result)
                    }
                }
            } else if (typeof onCancel === 'function') {
                const returned: unknown = onCancel()
                if (returned instanceof Promise) {
                    return returned.then(() => result)
                }
            }
            return result
        })
    }
}

export default preflight
